import { ReactNode } from 'react';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { useReducedMotion } from '@/components/animations';

export interface AuthBackgroundProps {
  children: ReactNode;
  variant?: 'auth' | 'onboarding';
}

export function AuthBackground({ children, variant = 'auth' }: AuthBackgroundProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const reduceMotion = useReducedMotion();
  const isDark = colorScheme === 'dark';

  // Different gradient colors based on variant
  const gradientColors = variant === 'onboarding'
    ? {
        light: { from: theme.colors.indigo[0], to: theme.colors.violet[0] },
        dark: { from: theme.colors.dark[8], to: theme.colors.dark[7] }
      }
    : {
        light: { from: theme.colors.gray[0], to: theme.colors.indigo[0] },
        dark: { from: theme.colors.dark[8], to: theme.colors.dark[9] }
      };

  const currentGradient = isDark ? gradientColors.dark : gradientColors.light;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(135deg, ${currentGradient.from} 0%, ${currentGradient.to} 100%)`,
        overflow: 'auto',
      }}
    >
      {/* Animated background elements */}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Floating orbs */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 100 + i * 50,
              height: 100 + i * 50,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.indigo[isDark ? 8 : 3]}, ${theme.colors.cyan[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${10 + i * 15}%`,
              left: `${5 + i * 20}%`,
              animation: reduceMotion ? 'none' : `float ${5 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </Box>

      {/* Content */}
      <Box style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
        {children}
      </Box>

      {/* Keyframe for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}

export default AuthBackground;
